/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment'
import { SessionStorageService } from '../services/session-storage.service';
import { APIResponseWrapper } from '../models/common/api-response-wrapper';
import { Company, DeliveryEvidencePolicy, OrderControlSettings, SingleInvoiceBilingualPolicy } from './company.model';
import {
    CompanyGroup,
    CompanyGroupInvitationAcceptanceContext,
    CompanyGroupMember,
    CompanyGroupMembershipAuditEvent,
    CompanyGroupMembershipInvitation,
    CreatedCompanyGroupMembershipInvitation
} from './company-group.model';
import { CrmClientRegistrationAttachment } from '../crm/crm.model';
import { Customer } from '../customer/customer.model';

@Injectable({
    providedIn: 'root'
})
export class CompanyService {

    restApiUrl: string

    constructor(private http: HttpClient, private sessionStorageService: SessionStorageService) {
        this.restApiUrl = `${environment.restApiUrl}`;
    }

    getCompanyById(companyId: number) {
        return this.http.get<APIResponseWrapper<Company>>(`${this.restApiUrl}company-endpoint/companies?companyId=${companyId}`);
    }

    getCompaniesByCountry(countryId: number, refresh: boolean = false) {
        const params: Record<string, string> = { countryId: countryId.toString() };
        if (refresh) {
            params['_'] = Date.now().toString();
        }
        return this.http.get<Company[]>(`${this.restApiUrl}company-endpoint/companies-by-country`, { params });
    }

    addCompany(company: Company) {
        const url = `${this.restApiUrl}company-endpoint/company`;
        return this.http.post<APIResponseWrapper<Company>>(url, company);
    }

    updateCompany(company: Company) {
        const url = `${this.restApiUrl}company-endpoint/company`;
        return this.http.put<APIResponseWrapper<Company>>(url, company);
    }

    inviteEmployee(companyId: number, request: Partial<Customer> & { roleIds?: number[] }) {
        return this.http.post<APIResponseWrapper<Customer>>(
            `${this.restApiUrl}company-endpoint/${companyId}/employee-invitations`,
            request
        );
    }

    getCompanyByCrNumber(crNumber: string) {
        return this.http.get<APIResponseWrapper<Company>>(`${this.restApiUrl}company-endpoint/companies?crNumber=${crNumber}`);
    }

    getOrderControlSettings(companyId: number) {
        return this.http.get<OrderControlSettings>(`${this.restApiUrl}orders/control-settings`, {
            params: { companyId: companyId.toString() }
        });
    }

    saveOrderControlSettings(companyId: number, settings: OrderControlSettings) {
        return this.http.put<OrderControlSettings>(`${this.restApiUrl}orders/control-settings`, settings, {
            params: { companyId: companyId.toString() }
        });
    }

    getDeliveryEvidencePolicy(companyId: number) {
        return this.http.get<DeliveryEvidencePolicy>(
            `${this.restApiUrl}company-endpoint/companies/${companyId}/delivery-evidence-policy`
        );
    }

    saveDeliveryEvidencePolicy(companyId: number, enabled: boolean) {
        return this.http.put<DeliveryEvidencePolicy>(
            `${this.restApiUrl}company-endpoint/companies/${companyId}/delivery-evidence-policy`,
            { enabled }
        );
    }

    getSingleInvoiceBilingualPolicy(companyId: number) {
        return this.http.get<SingleInvoiceBilingualPolicy>(
            `${this.restApiUrl}company-endpoint/companies/${companyId}/single-invoice-bilingual-policy`
        );
    }

    saveSingleInvoiceBilingualPolicy(companyId: number, required: boolean) {
        return this.http.put<SingleInvoiceBilingualPolicy>(
            `${this.restApiUrl}company-endpoint/companies/${companyId}/single-invoice-bilingual-policy`,
            { required }
        );
    }

    getCompanyGroups(active: boolean = true, refresh: boolean = false) {
        const params: Record<string, string> = { active: String(active) };
        if (refresh) {
            params['_'] = Date.now().toString();
        }
        return this.http.get<CompanyGroup[]>(`${this.restApiUrl}company-endpoint/company-groups`, { params });
    }

    getCompanyGroup(groupId: number) {
        return this.http.get<CompanyGroup>(`${this.restApiUrl}company-endpoint/company-groups/${groupId}`);
    }

    saveCompanyGroup(companyGroup: Partial<CompanyGroup>) {
        return this.http.post<CompanyGroup>(`${this.restApiUrl}company-endpoint/company-groups`, companyGroup);
    }

    createCompanyGroupMembershipInvitation(groupId: number, targetCompanyId: number) {
        return this.http.post<CreatedCompanyGroupMembershipInvitation>(
            `${this.restApiUrl}company-groups/${groupId}/membership-invitations`,
            { targetCompanyId }
        );
    }

    getCompanyGroupMembershipInvitations(groupId: number) {
        return this.http.get<CompanyGroupMembershipInvitation[]>(
            `${this.restApiUrl}company-groups/${groupId}/membership-invitations`
        );
    }

    getCompanyGroupMembershipAuditEvents(groupId: number) {
        return this.http.get<CompanyGroupMembershipAuditEvent[]>(
            `${this.restApiUrl}company-groups/${groupId}/membership-audit-events`
        );
    }

    cancelCompanyGroupMembershipInvitation(invitationId: number) {
        return this.http.post<CompanyGroupMembershipInvitation>(
            `${this.restApiUrl}company-groups/membership-invitations/${invitationId}/cancel`, {}
        );
    }

    revokeCompanyGroupMembership(groupId: number, companyId: number, reason?: string) {
        return this.http.post<void>(
            `${this.restApiUrl}company-groups/${groupId}/members/${companyId}/revoke`, { reason }
        );
    }

    getCompanyGroupInvitationAcceptanceContext(token: string) {
        return this.http.post<CompanyGroupInvitationAcceptanceContext>(
            `${this.restApiUrl}company-group-membership-invitations/acceptance-context`, { token }
        );
    }

    enableCompanyGroupInvitationApprovalAccess(token: string) {
        return this.http.post<CompanyGroupInvitationAcceptanceContext>(
            `${this.restApiUrl}company-group-membership-invitations/enable-approval-access`, { token }
        );
    }

    acceptCompanyGroupMembershipInvitation(token: string) {
        return this.http.post<CompanyGroupMembershipInvitation>(
            `${this.restApiUrl}company-group-membership-invitations/accept`, { token }
        );
    }

    declineCompanyGroupMembershipInvitation(token: string) {
        return this.http.post<CompanyGroupMembershipInvitation>(
            `${this.restApiUrl}company-group-membership-invitations/decline`, { token }
        );
    }

    getCompanyGroupCompanies(groupId: number) {
        return this.http.get<Company[]>(`${this.restApiUrl}company-endpoint/company-groups/${groupId}/companies`);
    }

    resolveCompanyGroupOwningCompanyIds(groupId: number) {
        return this.http.get<number[]>(`${this.restApiUrl}company-endpoint/company-groups/${groupId}/owning-company-ids`);
    }

    getCompanyRegistrationAttachments(ownerCompanyId: number, companyId: number = this.sessionStorageService.getCompany()?.id || ownerCompanyId) {
        return this.http.get<CrmClientRegistrationAttachment[]>(`${this.restApiUrl}crm/company/${ownerCompanyId}/registration-attachments?companyId=${companyId}`);
    }

    saveCompanyRegistrationAttachment(ownerCompanyId: number, attachment: CrmClientRegistrationAttachment, companyId: number = this.sessionStorageService.getCompany()?.id || ownerCompanyId) {
        return this.http.post<APIResponseWrapper<CrmClientRegistrationAttachment>>(`${this.restApiUrl}crm/company/${ownerCompanyId}/registration-attachments?companyId=${companyId}`, attachment);
    }

    deleteCompanyRegistrationAttachment(ownerCompanyId: number, attachmentId: number, companyId: number = this.sessionStorageService.getCompany()?.id || ownerCompanyId) {
        return this.http.delete<APIResponseWrapper<void>>(`${this.restApiUrl}crm/company/${ownerCompanyId}/registration-attachments/${attachmentId}?companyId=${companyId}`);
    }


}
